/**
 * CompassOverlay — full-screen compass UI that points the user toward a
 * selected pin using the device heading.
 *
 * Computes bearing via haversine + bearingDeg utils. Activated from the
 * compass chip in DetailSheetWrapper; rendered as an overlay above the map.
 */
import React from "react";
import { X } from "lucide-react";
import type { Lang } from "../types/index.ts";
import { t } from "../i18n/index.ts";
import { haversineKm, bearingDeg } from "../utils/geo.ts";

export function CompassOverlay({ target, userPos, heading, lang, onClose }: {
  target: { name: string; lat: number; lng: number };
  userPos: [number, number]; // [lng, lat]
  heading: number | null;
  lang: Lang;
  onClose: () => void;
}) {
  const distKm = haversineKm(userPos[1], userPos[0], target.lat, target.lng);
  const bearing = bearingDeg(userPos[1], userPos[0], target.lat, target.lng);
  const arrived = distKm < 0.01; // < 10m
  const arrowRotation = heading !== null ? (bearing - heading + 360) % 360 : bearing;
  const arrowColor = arrived ? "#43a047" : "#60a5fa";

  const distText = arrived
    ? t("compass.arrived", lang)
    : distKm < 1
      ? `${Math.round(distKm * 1000)} ${t("compass.meters", lang)}`
      : `${distKm.toFixed(1)} ${t("compass.km", lang)}`;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 40,
      background: "rgba(0,0,0,0.85)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      color: "#fff", fontFamily: "inherit",
    }}>
      {/* Close button */}
      <button
        onClick={onClose}
        style={{
          position: "absolute", top: 16, right: 16, zIndex: 41,
          width: 44, height: 44, borderRadius: 22,
          background: "rgba(255,255,255,0.1)", border: "none", color: "#fff",
          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
        }}
        title={t("compass.close", lang)}
      >
        <X size={20} strokeWidth={2.5} />
      </button>

      {/* Pin name */}
      <div style={{
        fontSize: 18, fontWeight: 700, letterSpacing: 0.5,
        textAlign: "center", padding: "0 40px", marginBottom: 40,
        maxWidth: 320, lineHeight: 1.4,
      }}>
        {target.name}
      </div>

      {/* Compass arrow */}
      <div style={{ marginBottom: 40 }}>
        <svg
          width="200" height="200" viewBox="0 0 200 200"
          style={{ transition: "transform 0.3s ease-out", transform: `rotate(${arrowRotation}deg)` }}
        >
          <circle cx="100" cy="100" r="95" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="2" />
          <circle cx="100" cy="100" r="80" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
          {/* Arrow pointing up (north = 0\u00b0), rotated by arrowRotation */}
          <polygon
            points="100,15 120,100 100,85 80,100"
            fill={arrowColor}
            opacity={arrived ? 0.6 : 1}
          />
          {/* Arrow tail */}
          <polygon
            points="100,185 120,100 100,115 80,100"
            fill="rgba(255,255,255,0.15)"
          />
          {/* Center dot */}
          <circle cx="100" cy="100" r="6" fill={arrowColor} />
        </svg>
      </div>

      {/* Distance */}
      <div style={{
        fontSize: arrived ? 22 : 36, fontWeight: 900, letterSpacing: 1,
        color: arrived ? "#43a047" : "#fff",
        marginBottom: 8,
      }}>
        {distText}
      </div>

      {/* No compass fallback note */}
      {heading === null && (
        <div style={{
          fontSize: 11, color: "rgba(255,255,255,0.4)",
          marginTop: 16, textAlign: "center", padding: "0 40px",
        }}>
          {t("compass.noCompass", lang)}
        </div>
      )}
    </div>
  );
}
